/** Local calculation work survives navigation without becoming an outcome or
 * entering semantic history. Unknown artifact versions remain inert text. */
import {
  AUTHORED_COMPUTATIONAL_CASES,
  COMPUTATIONAL_LIMITS,
  inspectComputationalArtifact,
  type ComputationalArtifactInspection,
} from './computationalArtifact'

export const COMPUTATIONAL_WORKSPACE_VERSION = 1 as const
export const COMPUTATIONAL_WORKSPACE_MAX_BYTES = 2_000_000

export interface ComputationalDraft {
  input: string
  exampleId: string | null
  nodeId: string
  description: string
  reported: boolean
}

export interface ComputationalWorkspaceState {
  version: typeof COMPUTATIONAL_WORKSPACE_VERSION
  draft: ComputationalDraft
  /** Original imported bytes are retained as text even when inspection refuses
   * their version, recipe, syntax or declared semantics. They are never code. */
  current: { origin: 'local' | 'imported'; text: string } | null
}

const bytes = (text: string) => new TextEncoder().encode(text).byteLength
const boundedText = (value: unknown, max = COMPUTATIONAL_LIMITS.maxStringLength): value is string =>
  typeof value === 'string' && value.length <= max

function record(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  if (
    !value ||
    typeof value !== 'object' ||
    (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) ||
    Object.getOwnPropertySymbols(value).length
  )
    return false
  const descriptors = Object.getOwnPropertyDescriptors(value)
  return (
    Object.keys(descriptors).length === keys.length &&
    keys.every((key) => {
      const descriptor = descriptors[key]
      return !!descriptor && 'value' in descriptor && descriptor.enumerable === true
    })
  )
}

export function createComputationalWorkspace(): ComputationalWorkspaceState {
  const example = AUTHORED_COMPUTATIONAL_CASES[0]!
  return {
    version: 1,
    draft: {
      input: JSON.stringify(example.inputs, null, 2),
      exampleId: example.id,
      nodeId: '',
      description: example.description,
      reported: false,
    },
    current: null,
  }
}

/** Draft syntax may be unfinished. Structural and byte ceilings still apply,
 * and an authored-example declaration must refer to the unchanged example. */
export function parseComputationalWorkspace(value: unknown): ComputationalWorkspaceState | null {
  if (!record(value, ['version', 'draft', 'current']) || value.version !== 1) return null
  const draft = value.draft
  if (
    !record(draft, ['input', 'exampleId', 'nodeId', 'description', 'reported']) ||
    typeof draft.input !== 'string' ||
    draft.input.length > COMPUTATIONAL_LIMITS.maxInputBytes ||
    bytes(draft.input) > COMPUTATIONAL_LIMITS.maxInputBytes ||
    !boundedText(draft.nodeId) ||
    !boundedText(draft.description) ||
    typeof draft.reported !== 'boolean'
  )
    return null
  if (draft.exampleId !== null) {
    const example = AUTHORED_COMPUTATIONAL_CASES.find((row) => row.id === draft.exampleId)
    if (!example || draft.reported) return null
    try {
      if (JSON.stringify(JSON.parse(draft.input)) !== JSON.stringify(example.inputs)) return null
    } catch {
      return null
    }
  }
  let current: ComputationalWorkspaceState['current'] = null
  if (value.current !== null) {
    const source = value.current
    if (
      !record(source, ['origin', 'text']) ||
      (source.origin !== 'local' && source.origin !== 'imported') ||
      typeof source.text !== 'string' ||
      source.text.length > COMPUTATIONAL_LIMITS.maxInputBytes ||
      bytes(source.text) > COMPUTATIONAL_LIMITS.maxInputBytes
    )
      return null
    current = { origin: source.origin, text: source.text }
  }
  // Explicit projection both detaches data and avoids invoking a supplied toJSON.
  const normalized: ComputationalWorkspaceState = {
    version: 1,
    draft: {
      input: draft.input,
      exampleId: draft.exampleId as string | null,
      nodeId: draft.nodeId,
      description: draft.description,
      reported: draft.reported,
    },
    current,
  }
  return bytes(JSON.stringify(normalized)) <= COMPUTATIONAL_WORKSPACE_MAX_BYTES ? normalized : null
}

/** Re-open an existing result through the same allowlist as a fresh import. */
export function inspectComputationalWorkspaceCurrent(
  workspace: ComputationalWorkspaceState,
): ComputationalArtifactInspection | null {
  if (!workspace.current) return null
  try {
    const inspection = inspectComputationalArtifact(JSON.parse(workspace.current.text))
    if (!inspection.ok || workspace.current.origin === 'imported') return inspection
    const artifact = inspection.artifact
    const { draft } = workspace
    const inputStatus = draft.exampleId
      ? 'synthetic'
      : draft.reported
        ? 'reported-observations'
        : 'hypothetical'
    if (
      JSON.stringify(artifact.inputs) !== JSON.stringify(JSON.parse(draft.input)) ||
      artifact.provenance.origin !== (draft.exampleId ? 'authored-example' : 'user-supplied') ||
      artifact.provenance.inputStatus !== inputStatus ||
      artifact.provenance.description !== draft.description ||
      artifact.context.nodeId !== (draft.nodeId || null)
    )
      return {
        ok: false,
        issues: ['The saved local result does not match its retained draft inputs or provenance.'],
      }
    return inspection
  } catch {
    return {
      ok: false,
      issues: ['The retained artifact is not valid JSON. Its text is preserved.'],
    }
  }
}
